/**
 * useShapeResize Hook
 *
 * Provides resize functionality for shapes by dragging corner/vertex handles.
 * Supports smooth real-time preview with final Zustand update.
 *
 * Features:
 * - Drag corner handles to resize Rectangle
 * - Drag cardinal points (N, S, E, W) to resize Circle
 * - Drag vertices to resize Triangle
 * - Real-time preview (updates MapLibre source directly)
 * - Single Zustand update on drag end (triggers history)
 * - No size constraints (allows shapes of any size)
 *
 * @module hooks/useShapeResize
 */

import { useCallback, useRef } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import maplibregl from 'maplibre-gl';
import type { MarkerDragEvent } from '@vis.gl/react-maplibre';
import { useDrawingTools } from './useDrawingTools';
import { useGeoMarkupStore } from '../store';
import { ShapeType } from '../types/geometry';
import { createShapeGeoJson } from '../components/map/GeometryRenderer/ShapeRenderer/utils/shapeGeoJsonHelpers';
import {
  calculateRectanglePoints,
  calculateCircleCardinalPoints,
  calculateCenter,
  calculateDistance,
} from '../utils/shapeResizeHelpers';
import type { Coordinates } from '../types';
import { useGeometryAutoPan } from './useGeometryAutoPan';

/**
 * Custom hook for shape resize functionality
 *
 * @param geometryId - The ID of the shape geometry
 * @returns Object containing resize handlers
 */
export const useShapeResize = (geometryId: string) => {
  const { current: map } = useMap();
  const { updateGeometry, currentGeometries } = useDrawingTools();

  // Zustand drag state management
  const setDraggingGeometry = useGeoMarkupStore((s) => s.setDraggingGeometry);

  // Auto-pan support for smooth dragging experience
  const { smartPan, initializeVelocityTracking, stopAutoPan } = useGeometryAutoPan(geometryId);

  // Find the specific geometry we're working with
  const geometry = currentGeometries.find((g) => g.id === geometryId);

  // Track which handle is being dragged (for visibility control)
  const draggedHandleIndexRef = useRef<number | null>(null);

  /**
   * Handle resize start event
   * Sets dragging state to prevent conflicts with other interactions
   *
   * @param pointIndex - Index of the handle being dragged
   */
  const handleResizeStart = useCallback(
    (event: MarkerDragEvent, pointIndex: number) => {
      if (!geometry) return;

      // Track which handle is being dragged
      draggedHandleIndexRef.current = pointIndex;

      // Set dragging state in Zustand
      setDraggingGeometry(geometryId);

      // Initialize velocity tracking for auto-pan
      initializeVelocityTracking(event.lngLat);
    },
    [geometry, geometryId, setDraggingGeometry, initializeVelocityTracking]
  );

  /**
   * Handle resize drag event (REAL-TIME PREVIEW)
   * Updates MapLibre source directly for 60fps smooth preview
   * NO Zustand updates during drag - only at the end
   *
   * @param event - Drag event from MapLibre marker
   * @param pointIndex - Index of the handle being dragged
   */
  const handleResize = useCallback(
    (event: MarkerDragEvent, pointIndex: number) => {
      if (!geometry || !map) return;

      const shapePoints = geometry.shapePoints || [];
      if (shapePoints.length === 0) return;

      const newCoords = event.lngLat;
      const newPoint: Coordinates = {
        lat: newCoords.lat,
        lng: newCoords.lng,
      };

      // Auto-pan map when dragging near edges
      smartPan(event.lngLat);

      let newShapePoints: Coordinates[] = [];

      switch (geometry.shapeType) {
        case ShapeType.Rectangle: {
          // Rectangle: 4 corners [TL, TR, BR, BL]
          // Dragging one corner affects 2 dimensions
          const oppositeIndex = (pointIndex + 2) % 4; // TL<->BR, TR<->BL
          const oppositePoint = shapePoints[oppositeIndex];

          // Validate opposite point exists
          if (!oppositePoint) {
            return;
          }

          // Reconstruct rectangle from dragged and opposite corners
          newShapePoints = calculateRectanglePoints(newPoint, oppositePoint);
          break;
        }

        case ShapeType.Circle: {
          // Circle: 4 cardinal points [North, South, East, West]
          // Calculate current center from all 4 points
          const center = calculateCenter(shapePoints);

          // Calculate new radius based on which point is being dragged
          const newRadius = calculateDistance(center, newPoint);

          // Update all 4 cardinal points with new radius
          newShapePoints = calculateCircleCardinalPoints(center, newRadius);
          break;
        }

        case ShapeType.Triangle: {
          // Triangle: 3 vertices [top, bottom-left, bottom-right]
          // Update the dragged vertex
          newShapePoints = [...shapePoints];
          newShapePoints[pointIndex] = newPoint;
          break;
        }

        default:
          return;
      }

      // Update MapLibre source directly (60fps without React re-renders)
      const source = map.getSource(`shape-source-${geometryId}`) as maplibregl.GeoJSONSource;
      if (source && source.type === 'geojson') {
        // ✅ DRY: Reuse createShapeGeoJson (auto-applies shape type logic!)
        const previewGeometry = { ...geometry, shapePoints: newShapePoints };
        const geoJson = createShapeGeoJson({ geometry: previewGeometry });
        source.setData(geoJson);
      }
    },
    [geometry, geometryId, map, smartPan]
  );

  /**
   * Handle resize end event
   * FINAL UPDATE - Only update Zustand once at the end
   * This is the ONLY place where we update geometry during resize
   */
  const handleResizeEnd = useCallback(
    (event: MarkerDragEvent, pointIndex: number) => {
      if (!geometry || !map) return;

      const shapePoints = geometry.shapePoints || [];
      if (shapePoints.length === 0) return;

      // Stop auto-panning
      stopAutoPan();

      const newCoords = event.lngLat;
      const newPoint: Coordinates = {
        lat: newCoords.lat,
        lng: newCoords.lng,
      };

      let newShapePoints: Coordinates[] = [];

      // Calculate final shape points (same logic as handleResize)
      switch (geometry.shapeType) {
        case ShapeType.Rectangle: {
          const oppositeIndex = (pointIndex + 2) % 4;
          const oppositePoint = shapePoints[oppositeIndex];

          if (!oppositePoint) return;

          // Reconstruct rectangle from dragged and opposite corners
          newShapePoints = calculateRectanglePoints(newPoint, oppositePoint);
          break;
        }

        case ShapeType.Circle: {
          const center = calculateCenter(shapePoints);
          const newRadius = calculateDistance(center, newPoint);

          // Update all 4 cardinal points with new radius
          newShapePoints = calculateCircleCardinalPoints(center, newRadius);
          break;
        }

        case ShapeType.Triangle: {
          newShapePoints = [...shapePoints];
          newShapePoints[pointIndex] = newPoint;
          break;
        }

        default:
          return;
      }

      // Recalculate center
      const newCenter = calculateCenter(newShapePoints);

      // Single Zustand update with final position (triggers history)
      updateGeometry(geometryId, {
        shapePoints: newShapePoints,
        calculatedCenter: newCenter,
      });

      // Clear dragging state
      setDraggingGeometry(null);
      draggedHandleIndexRef.current = null;
    },
    [geometry, geometryId, map, updateGeometry, setDraggingGeometry, stopAutoPan]
  );

  return {
    handleResizeStart,
    handleResize,
    handleResizeEnd,
    draggedHandleIndexRef, // Expose for visibility control
  };
};
